<template>
	<ModalV2
		:value="value && visible"
		:title="$t('application.decline.choose-template')"
		:subtitle="
			$t('application.decline.recipient-count', { count: recipientCount })
		"
		size="xxl"
		panel-class="flex max-h-[calc(100dvh-32px)] flex-col"
		body-class="min-h-0 flex-1 px-0 pb-0"
		footer-class="shrink-0 border-t border-grey-200 p-4"
		:close-on-backdrop="!submitting"
		:close-on-esc="!submitting"
		@input="!$event && close()"
	>
		<div class="flex items-center gap-3 border-b border-grey-200 px-6 pb-4">
			<div class="flex -space-x-2">
				<Avatar
					v-for="application in applications.slice(0, 3)"
					:key="application.id"
					:image="
						application.applicant_profile &&
							application.applicant_profile.user &&
							application.applicant_profile.user.avatar
					"
					:fallback="initials(application)"
					size="30px"
					class="rounded-full ring-2 ring-white"
				/>
			</div>
			<span class="text-sm text-grey-900">{{
				$t(
					applications.length === 1
						? 'application.decline.applicant-selected'
						: 'application.decline.applicants-selected',
					{
						count: applications.length
					}
				)
			}}</span>
		</div>
		<div
			v-if="errorKey"
			role="alert"
			class="m-4 rounded-lg bg-grey-50 p-3 text-sm text-error"
		>
			{{ $t(errorKey) }}
			<Button
				v-if="!attemptPayload"
				type="link"
				@click="refreshPreview"
				>{{ $t('application.decline.refresh') }}</Button
			>
		</div>

		<div
			v-if="mode === 'choose'"
			class="grid min-w-0 grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)]"
		>
			<div
				class="min-w-0 border-b border-grey-200 p-4 md:border-b-0 md:border-r"
			>
				<TemplateSelector
					v-model="selectedId"
					:search-templates="searchTemplates"
					:disabled="submitting || !!attemptPayload"
				/>
			</div>
			<div class="min-w-0 space-y-4 p-4">
				<div
					v-if="context"
					class="flex items-center justify-between gap-3"
				>
					<h3
						class="m-0 font-sans text-sm font-semibold normal-case text-grey-1000"
					>
						{{ context.template.name }}
					</h3>
					<span
						v-if="context.template.is_default"
						class="rounded-full bg-grey-50 px-3 py-1 text-xs text-grey-700"
						>{{ $t('email-templates.default') }}</span
					>
				</div>
				<p v-if="context" class="m-0 text-xs text-grey-600">
					{{
						$t('application.decline.sample-recipient', {
							name: context.sample.name
						})
					}}
				</p>
				<AddressChips
					v-if="context && !context.template.bcc.length"
					:key="selectedId"
					ref="bcc"
					v-model="manualBcc"
					:id="`rejection-bcc-${_uid}`"
					:limit="3"
					:disabled="submitting || !!attemptPayload"
					:label="$t('email-templates.bcc')"
					:help="$t('application.decline.bcc-help')"
				/>
				<TemplatePreview
					:preview="context && context.preview"
					:loading="previewLoading"
					:error="!!errorKey && !attemptPayload"
					:bcc="effectiveBcc"
				/>
			</div>
		</div>
		<template #footer>
			<Button type="secondary" :disabled="submitting" @click="close">{{
				$t(
					result
						? 'application.decline.close'
						: 'email-templates.cancel'
				)
			}}</Button>
			<Button
				v-if="
					!attemptPayload ||
						errorKey === 'application.decline.recipients-changed'
				"
				type="danger"
				:disabled="
					submitting ||
						previewLoading ||
						(!attemptPayload &&
							mode !== 'none' &&
							!!errorKey &&
							errorKey !==
								'application.decline.recipients-changed')
				"
				:loading="submitting"
				@click="submit"
			>
				{{
					$t(
						mode === 'none'
							? 'application.decline.without-email'
							: 'application.decline.send-decline'
					)
				}}
			</Button>
		</template>
	</ModalV2>
</template>
<script>
import ModalV2 from '~/components/Modal/ModalV2'
import Button from '~/components/ui/common/Button'
import Avatar from '~/components/avatar'
import TemplateSelector from '~/components/EmailTemplates/TemplateSelector'
import TemplatePreview from '~/components/EmailTemplates/TemplatePreview'
import AddressChips from '~/components/EmailTemplates/AddressChips'

function requestId() {
	return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
		(
			Number(c) ^
			(crypto.getRandomValues(new Uint8Array(1))[0] &
				(15 >> (Number(c) / 4)))
		).toString(16)
	)
}
export default {
	components: {
		ModalV2,
		Button,
		Avatar,
		TemplateSelector,
		TemplatePreview,
		AddressChips
	},
	props: {
		value: Boolean,
		applications: { type: Array, required: true },
		initialContext: Object,
		mode: { type: String, default: 'choose' }
	},
	data() {
		return {
			context: this.initialContext,
			selectedId: this.initialContext?.template.id,
			manualBcc: [],
			previewLoading: false,
			submitting: false,
			errorKey: '',
			result: null,
			resultHandled: false,
			disposed: false,
			attemptPayload: null,
			request: 0,
			poll: null,
			visible: this.mode === 'choose'
		}
	},
	computed: {
		applicationIds() {
			return this.applications.map(application => application.id)
		},
		recipientCount() {
			return this.mode === 'none'
				? 0
				: this.context?.recipient_ids.length || 0
		},
		effectiveBcc() {
			return this.context?.template.bcc.length
				? this.context.template.bcc
				: this.manualBcc
		}
	},
	watch: {
		selectedId() {
			this.manualBcc = []
			this.refreshPreview()
		},
		manualBcc() {
			if (!this.attemptPayload) this.refreshPreview()
		}
	},
	mounted() {
		if (this.mode !== 'choose') this.submit()
		this.poll = setInterval(() => {
			if (!this.attemptPayload && !this.submitting) this.refreshPreview()
		}, 15000)
		window.addEventListener('focus', this.onFocus)
	},
	beforeDestroy() {
		this.disposed = true
		clearInterval(this.poll)
		this.request++
		window.removeEventListener('focus', this.onFocus)
		if (!this.submitting) return this.reconcileDetachedResult()
	},
	methods: {
		initials(application) {
			const user = application.applicant_profile?.user
			return [user?.firstname, user?.lastname]
				.filter(Boolean)
				.map(name => name[0])
				.join('')
		},
		onFocus() {
			if (!this.attemptPayload && !this.submitting) this.refreshPreview()
		},
		searchTemplates(search, page) {
			return this.$store.dispatch('rejection/templates', {
				application_ids: this.applicationIds,
				search,
				page
			})
		},
		async refreshPreview() {
			if (this.attemptPayload || this.submitting || this.mode === 'none')
				return
			const request = ++this.request
			this.previewLoading = true
			try {
				const context = await this.$store.dispatch(
					'rejection/preview',
					{
						application_ids: this.applicationIds,
						...(this.mode === 'choose'
							? { template_id: this.selectedId }
							: {}),
						bcc: this.context?.template.bcc.length
							? []
							: this.manualBcc
					}
				)
				if (request !== this.request) return
				this.context = context
				this.errorKey = ''
			} catch (error) {
				if (request !== this.request) return
				this.errorKey =
					error.response?.status === 422
						? 'application.decline.stale-template'
						: 'application.decline.request-failed'
			} finally {
				if (request === this.request) this.previewLoading = false
			}
		},
		async submit() {
			if (
				this.submitting ||
				this.previewLoading ||
				this.resultHandled ||
				(this.attemptPayload &&
					this.errorKey !== 'application.decline.recipients-changed')
			)
				return
			if (!this.attemptPayload && this.$refs.bcc && !this.$refs.bcc.add())
				return
			this.submitting = true
			if (!this.attemptPayload)
				this.attemptPayload = {
					request_id: requestId(),
					application_ids: this.applicationIds,
					send_email: this.mode !== 'none',
					...(this.mode === 'choose'
						? { template_id: this.selectedId }
						: {}),
					bcc:
						this.mode === 'choose' &&
						!this.context?.template.bcc.length
							? [...this.manualBcc]
							: [],
					recipient_ids:
						this.mode === 'none'
							? []
							: this.context?.recipient_ids || []
				}
			try {
				this.result = await this.$store.dispatch(
					'rejection/send',
					this.attemptPayload
				)
				this.resultHandled = false
				this.errorKey = ''
				this.complete()
			} catch (error) {
				this.visible = true
				if (error.response?.data?.code === 'recipients_changed') {
					this.attemptPayload = {
						...this.attemptPayload,
						recipient_ids: error.response.data.recipient_ids
					}
					this.context = {
						...this.context,
						recipient_ids: error.response.data.recipient_ids
					}
					this.errorKey = 'application.decline.recipients-changed'
				} else if ([403, 422].includes(error.response?.status)) {
					this.attemptPayload = null
					this.errorKey = Object.keys(
						error.response.data?.errors || {}
					).some(key => key === 'bcc' || key.startsWith('bcc.'))
						? 'email-templates.invalid-email'
						: error.response.status === 422
						? 'application.decline.stale-template'
						: 'application.decline.request-failed'
				} else this.errorKey = 'application.action.unconfirmed'
			} finally {
				this.submitting = false
				await this.reconcileDetachedResult()
			}
		},
		reconcileDetachedResult() {
			if (!this.disposed || !this.result || this.resultHandled) return
			// The request outlives its chooser when selection or navigation removes it.
			this.resultHandled = true
			return this.$store.dispatch('application/applyRejectionResult', {
				...this.result,
				refreshList: true
			})
		},
		complete() {
			if (this.disposed || this.resultHandled) return
			this.resultHandled = true
			this.$emit('complete', this.result)
		},
		close() {
			if (!this.submitting)
				this.result ? this.complete() : this.$emit('close')
		}
	}
}
</script>
